/**
 * CREATIVE DETONATOR - Global Header Injection
 *
 * Injects consistent header HTML across all public pages.
 * Dashboard pages use their own sidebar layout and should NOT use this module.
 * Auth pages use minimal layout and should NOT use this module.
 */

import { getPathPrefix, getCurrentPage, isDashboardPage, isAuthPage } from './layout-utils.js';

/**
 * Generate header HTML with correct paths and active states
 * Phase 1: Auth elements are ALWAYS included for consistent header across all pages
 * GENERATE button is hidden by default for logged-out users (controlled by CSS + auth UI)
 *
 * @param {string} prefix - Path prefix for assets
 * @param {string} activePage - Current page identifier for active state
 * @param {boolean} includeAuthElements - DEPRECATED: Auth elements are always included now
 * @returns {string} Header HTML
 */
function getHeaderHTML(prefix, activePage, includeAuthElements = false) {
  const activeClass = (page) => activePage === page ? 'active' : '';

  // Phase 1: Profile widget is ALWAYS included (visibility controlled by auth state in ui.js)
  // Clean, unified block: [Credits] [Avatar with dropdown]
  const profileWidgetHTML = `
        <!-- Profile Widget (populated by auth state) -->
        <div class="profile-widget profile-widget-header" id="profile-widget" style="display: none;">
          <a href="${prefix}features/dashboard/pages/credits.html" class="profile-credits" title="Credits">
            <span class="pixel-coin-static sm"></span>
            <span class="credits-value">0</span>
          </a>
          <button class="profile-trigger" id="profile-trigger" title="Account Menu">
            <span class="profile-avatar">U</span>
          </button>
          <div class="profile-dropdown">
            <a href="${prefix}features/dashboard/pages/index.html" class="profile-item"><span class="profile-item-icon"><img src="${prefix}assets/icons/icon_0042_home.png" alt=""></span>Dashboard</a>
            <a href="${prefix}features/dashboard/pages/account.html" class="profile-item"><span class="profile-item-icon"><img src="${prefix}assets/icons/icon_0047_user-account-profile.png" alt=""></span>Account</a>
            <a href="${prefix}features/dashboard/pages/credits.html" class="profile-item"><span class="profile-item-icon"><span class="pixel-coin-static xs"></span></span>Credits</a>
            <div class="profile-divider"></div>
            <button class="profile-item profile-logout" id="logout-btn"><span class="profile-item-icon"><img src="${prefix}assets/icons/icon_0001_lock-black.png" alt=""></span>Logout</button>
          </div>
        </div>`;

  return `
<header class="header">
  <div class="header-container">
    <a href="${prefix}index.html" class="logo">
      <img src="${prefix}assets/creativedetonator_logo_icon_black.svg" alt="" class="logo-icon"
           data-normal="${prefix}assets/creativedetonator_logo_icon_black.svg"
           data-pressed="${prefix}assets/creativedetonator_logo_icon_black-pressed.svg">
      <span class="logo-text">CREATIVE DETONATOR</span>
    </a>
    <nav class="nav">
      <a href="${prefix}features/method/pages/index.html" class="nav-link ${activeClass('method')}">THE METHOD</a>
      <a href="${prefix}features/triggers/pages/index.html" class="nav-link ${activeClass('triggers')}">TRIGGERS</a>
      <a href="${prefix}about-method/index.html" class="nav-link ${activeClass('about')}">ABOUT</a>
      <a href="${prefix}features/generator/index.html" class="nav-link nav-cta nav-cta-auth ${activeClass('generator')}" data-auth-required style="display:none;">GENERATE</a>${profileWidgetHTML}
    </nav>
    <button class="mobile-menu-toggle" aria-label="Toggle menu" onclick="document.querySelector('.mobile-menu').classList.toggle('active'); this.classList.toggle('active'); document.body.style.overflow = document.querySelector('.mobile-menu').classList.contains('active') ? 'hidden' : '';">
      <span></span>
      <span></span>
      <span></span>
    </button>
  </div>
</header>

<!-- Mobile Menu -->
<div class="mobile-menu">
  <button class="mobile-menu-close" aria-label="Close menu" onclick="document.querySelector('.mobile-menu').classList.remove('active'); document.querySelector('.mobile-menu-toggle').classList.remove('active'); document.body.style.overflow = '';">&times;</button>
  <nav class="mobile-nav">
    <a href="${prefix}features/method/pages/index.html" class="mobile-nav-link">THE METHOD</a>
    <a href="${prefix}features/triggers/pages/index.html" class="mobile-nav-link">TRIGGERS</a>
    <a href="${prefix}about-method/index.html" class="mobile-nav-link">ABOUT</a>
    <a href="${prefix}features/generator/index.html" class="mobile-nav-link mobile-nav-cta nav-cta-auth" data-auth-required style="display:none;">GENERATE IDEAS</a>
    <a href="${prefix}features/dashboard/pages/index.html" class="mobile-nav-link nav-dashboard" data-auth-required style="display:none;">DASHBOARD</a>
  </nav>
</div>
`;
}

/**
 * Initialize mobile menu toggle functionality
 */
function initMobileMenu() {
  const toggle = document.querySelector('.mobile-menu-toggle');
  const mobileMenu = document.querySelector('.mobile-menu');
  const closeBtn = document.querySelector('.mobile-menu-close');
  const nav = document.querySelector('.nav');

  console.log('[MobileMenu] Init - toggle found:', !!toggle, 'menu found:', !!mobileMenu);

  const closeMenu = () => {
    toggle?.classList.remove('active');
    mobileMenu?.classList.remove('active');
    nav?.classList.remove('active');
    document.body.style.overflow = '';
  };

  const openMenu = () => {
    console.log('[MobileMenu] Opening menu!');
    toggle?.classList.add('active');
    mobileMenu?.classList.add('active');
    nav?.classList.add('active');
    document.body.style.overflow = 'hidden';
  };

  if (toggle) {
    console.log('[MobileMenu] Adding click listener to toggle');

    const handleToggle = (e) => {
      console.log('[MobileMenu] Toggle activated!', e.type);
      e.preventDefault();
      e.stopPropagation();
      if (mobileMenu?.classList.contains('active')) {
        closeMenu();
      } else {
        openMenu();
      }
    };

    toggle.addEventListener('click', handleToggle);
    toggle.addEventListener('touchend', handleToggle, { passive: false });

    // Make absolutely sure it's clickable
    toggle.style.pointerEvents = 'auto';
    toggle.style.touchAction = 'manipulation';
  } else {
    console.error('[MobileMenu] Toggle button NOT FOUND!');
  }

  // Close button
  if (closeBtn) {
    closeBtn.addEventListener('click', closeMenu);
  }

  // Close mobile menu when clicking a link
  if (mobileMenu) {
    mobileMenu.querySelectorAll('.mobile-nav-link').forEach(link => {
      link.addEventListener('click', closeMenu);
    });
  }

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileMenu?.classList.contains('active')) {
      closeMenu();
    }
  });
}

/**
 * Initialize logo press effect (classic Mac button behavior)
 */
function initLogoPressEffect() {
  const logoIcon = document.querySelector('.logo-icon');
  if (logoIcon && logoIcon.dataset.normal && logoIcon.dataset.pressed) {
    logoIcon.addEventListener('mousedown', () => {
      logoIcon.src = logoIcon.dataset.pressed;
    });
    logoIcon.addEventListener('mouseup', () => {
      logoIcon.src = logoIcon.dataset.normal;
    });
    logoIcon.addEventListener('mouseleave', () => {
      logoIcon.src = logoIcon.dataset.normal;
    });
  }
}

/**
 * Injects global header into page
 * @param {string} containerId - ID of placeholder element (default: 'global-header')
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeAuthElements - Include profile widget and login link
 */
export function injectHeader(containerId = 'global-header', options = {}) {
  // Skip injection for dashboard and auth pages
  if (isDashboardPage() || isAuthPage()) {
    console.log('[Layout] Skipping header injection for dashboard/auth page');
    return;
  }

  const container = document.getElementById(containerId);
  if (!container) {
    console.warn('[Layout] Header container not found:', containerId);
    return;
  }

  const prefix = getPathPrefix();
  const activePage = getCurrentPage();
  const includeAuthElements = options.includeAuthElements || false;

  container.innerHTML = getHeaderHTML(prefix, activePage, includeAuthElements);

  // Initialize interactive features
  initMobileMenu();
  initLogoPressEffect();

  // CRITICAL: Dispatch event so auth/ui.js knows header is ready
  // This prevents race conditions where auth tries to query elements
  // before they exist in the DOM
  window.dispatchEvent(new CustomEvent('layout:header-injected'));
  console.log('[Layout] Header injected, event dispatched');
}

/**
 * Auto-inject header on DOM ready if placeholder exists
 * Check for data-include-auth attribute on container to include auth elements
 */
function autoInject() {
  const container = document.getElementById('global-header');
  if (container) {
    const includeAuth = container.dataset.includeAuth === 'true';
    injectHeader('global-header', { includeAuthElements: includeAuth });
  }
}

// Auto-inject on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInject);
} else {
  autoInject();
}
